import { CommonModule } from '@angular/common'
import { Component, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router'
import { ComponentCanDeactivate } from '@app/core/guard/can-deactivate-guard'
import { CdnConfig } from '@app/core/model/cdn-config.model'
import { ChangeRequest } from '@app/core/model/change-request.model'
import { ApplicationSetting } from '@app/core/model/enums/application-setting.enum'
import { EventType } from '@app/core/model/enums/event-type.enum'
import { Timeout } from '@app/core/model/enums/timeout.enum'
import { ScopeTeamMember } from '@app/core/model/scope-team.model'
import { ScopeVersion, StatusType } from '@app/core/model/scope-version'
import { User } from '@app/core/model/user.model'
import { AuthService } from '@app/core/service/auth.service'
import { LanguageService } from '@app/core/service/language.service'
import { BootstrapActions } from '@app/core/store'
import { BootstrapSelectors } from '@app/core/store/selectors/bootstrap.selector'
import { CompanyManagementService } from '@app/features/company-management/service/company-management.service'
import { ScenarioService } from '@app/features/company-management/service/scenario.service'
import { ExportModalComponent } from '@app/features/scope-overview/components/export-modal/export-modal.component'
import { ChangeRequestSelectors } from '@app/features/scope-overview/change-request/store/selectors/change-request.selectors'
import { ScopeOverviewTab } from '@app/features/scope-overview/enum/scope-overview-tab.enum'
import { ExportModalConfig } from '@app/features/scope-overview/model/export-modal-config'
import { OutputTemplate } from '@app/features/scope-overview/model/output-template.model'
import { ScenarioQuestion } from '@app/features/scope-overview/model/scenario-question.model'
import { ApprovalFlowService } from '@app/features/scope-overview/service/approval-flow.service'
import { FindReplaceService } from '@app/features/scope-overview/service/find-replace.service'
import { ScopeOverviewNavigationService } from '@app/features/scope-overview/service/scope-overview-navigation.service'
import { ScopeOverviewService } from '@app/features/scope-overview/service/scope-overview.service'
import { ScopeTabService } from '@app/features/scope-overview/service/scope-tab.service'
import { ScopeTeamService } from '@app/features/scope-overview/service/scope-team.service'
import { ScopeTraffickingService } from '@app/features/scope-overview/service/scope-trafficking.service'
import { UserSettingsService } from '@app/features/scope-overview/service/user-settings.service'
import { ScopeOverviewActions } from '@app/features/scope-overview/store/actions/scope-overview.action'
import { ScopePaginatedOverviewActions } from '@app/features/scope-overview/store/actions/scope-paginated-overview.actions'
import { ScopeOverviewSelectors } from '@app/features/scope-overview/store/selectors/scope-overview.selector'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { ScopeDynamicFieldSettingModel } from '@app/features/scoping/models/scope-dynamic-field-setting.model'
import { ScopingSelectors } from '@app/features/scoping/store/selectors/scoping.selector'
import { LoaderModalModule } from '@app/shared/components/loader-modal/loader-modal.module'
import { PreviewOutputComponent } from '@app/shared/components/preview-output/preview-output.component'
import { ScopeUiTabComponent } from '@app/shared/components/ui-components/scope-ui-tab/scope-ui-tab.component'
import { ScopeUiTabsComponent } from '@app/shared/components/ui-components/scope-ui-tabs/scope-ui-tabs.component'
import { NotificationEvent, RegisteredSubscription, WebsocketService } from '@app/shared/services/websocket.service'
import { SharedModule } from '@app/shared/shared.module'
import { SnackbarEventType, SnackbarService } from '@app/shared/utils/snackbar.service'
import { untilDestroyed } from '@app/shared/utils/utils'
import { Store } from '@ngrx/store'
import { plainToInstance } from 'class-transformer'
import { cloneDeep } from 'lodash'
import { filter, Observable, take } from 'rxjs'
import { ScopeActivityComponent } from '../../scope-activity/scope-activity.component'
import { ScopeBreakdownComponent } from '../../scope-breakdown/scope-breakdown.component'
import { ScopeConfigurationCRComponent } from '../../scope-configuration/scope-configuration-cr.component'
import { ScopeConfigurationComponent } from '../../scope-configuration/scope-configuration.component'
import { ScopeDetailsModule } from '../../scope-details/scope-details.module'
import { ScopeOutputComponent } from '../../scope-output/scope-output.component'
import { TeamComponent } from '../../team/team.component'

@Component({
  selector: 'scope-overview-content',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    LoaderModalModule,
    ScopeUiTabComponent,
    ScopeUiTabsComponent,
    ScopeOutputComponent,
    TeamComponent,
    ScopeActivityComponent,
    ScopeBreakdownComponent,
    ScopeConfigurationComponent,
    ScopeConfigurationCRComponent,
    ScopeDetailsModule,
    RouterOutlet,
  ],
  templateUrl: './scope-overview-content.component.html',
  styleUrls: ['./scope-overview-content.component.scss'],
})
export class ScopeOverviewContentComponent implements OnDestroy, ComponentCanDeactivate, OnInit, OnChanges {
  @Input() currentScope: ScopeVersion
  @Input() changeRequests: ChangeRequest[]
  @Input() currentChangeRequest: ChangeRequest

  rateCard$: Observable<any> = this.store.select(ScopingSelectors.selectRateCard)
  cdnConfig$: Observable<CdnConfig | undefined> = this.store.select(BootstrapSelectors.selectCdnConfig)
  revertSummarySuccess$: Observable<any> = this.store.select(ScopingSelectors.selectRevertSummarySuccess)
  replaceSummarySuccess$: Observable<boolean> = this.store.select(ScopingSelectors.selectReplaceSummarySuccess)
  previewScope$: Observable<ScopeVersion> = this.store.select(ChangeRequestSelectors.selectPreviewScope)

  loggedInUser!: User | undefined
  cdnConfig!: CdnConfig
  companyIntegrations: any = []
  registeredSubs: RegisteredSubscription[] = []
  scenarioFields: ScenarioQuestion[]
  companyDynamicFieldSettings: ScopeDynamicFieldSettingModel[]

  configurationHasUpdates = true

  get currentTab(): string {
    return this.scopeOverviewNavigationService.currentTab
  }

  constructor(
    private store: Store,
    private snackbarService: SnackbarService,
    private scopeOverviewNavigationService: ScopeOverviewNavigationService,
    protected findReplaceService: FindReplaceService,
    protected scopeApprovalFlowService: ApprovalFlowService,
    protected route: ActivatedRoute,
    protected userSettingsService: UserSettingsService,
    private router: Router,
    private lang: LanguageService,
    private scopeOverviewService: ScopeOverviewService,
    private dialog: MatDialog,
    private traffickingService: ScopeTraffickingService,
    private webSocketService: WebsocketService,
    protected scopeTeamService: ScopeTeamService,
    private scopeTabService: ScopeTabService,
    private authService: AuthService,
    private manageService: CompanyManagementService,
    private scenarioService: ScenarioService
  ) {
    this.destroy$ = untilDestroyed()

    this.scopeApprovalFlowService.defaultStateChecks()

    this.store
      .select(ScopeOverviewSelectors.selectConfigurationHasUpdates)
      .pipe(this.destroy$())
      .subscribe((value: boolean) => (this.configurationHasUpdates = value))
  }

  ngOnInit(): void {
    this.loggedInUser = this.authService.loggedInUser

    this.scopeApprovalFlowService.updateApprovalStateChecks(this.currentScope)

    this.isPartOfScope(this.currentScope)

    if (!this.scopeApprovalFlowService.isStateDraft(this.currentScope.status))
      this.scopeOverviewService
        .getScopeCommentsStatsByIdAndVersion(this.currentScope.identity.id, this.currentScope.version)
        .subscribe({
          next: (data) => {
            this.scopeTabService.scopeCommentsData = data.deliverableCommentsCountMap
          },
          error: (error) => {
            this.snackbarService.showDefaultErrorSnackbar()
            return error
          },
        })

    if (this.registeredSubs.length == 0)
      this.registeredSubs.push(
        this.webSocketService.registerScopeEventReceiver(this.currentScope.identity.id, (event: any) =>
          this.onScopeEventReceived.call(this, event)
        )
      )

    this.userSettingsService.getCompanyIntegrations().subscribe((response) => {
      this.companyIntegrations = response
    })

    this.getThirdPartyBalance()
    this.getCdnConfig()
    this.store.dispatch(BootstrapActions.loadUserPreferences())
    this.retrieveCompanyDynamicFields()
    this.retrieveCompanyScenarioFields()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.currentScope)
      if (
        this.scopeApprovalFlowService.isStateConfigDraft(this.currentScope?.status) &&
        this.currentTab === ScopeOverviewTab.SCOPE
      )
        this.scopeOverviewNavigationService.selectTab(ScopeOverviewTab.CONFIGURATION)
  }

  retrieveCompanyDynamicFields() {
    this.manageService.retrieveCompanyDynamicFields().subscribe((dynamicFields) => {
      this.companyDynamicFieldSettings = dynamicFields
    })
  }

  retrieveCompanyScenarioFields() {
    this.scenarioService.getAll(true).subscribe((scenario) => {
      this.scenarioFields = scenario.flatMap((s) => s.categories).flatMap((c) => c.questions)
    })
  }

  isPartOfScope(currentScope: ScopeVersion) {
    if (currentScope == null || this.loggedInUser == null) {
      return
    }
    if (!currentScope.identity.team.hasUser(this.loggedInUser.id)) {
      const message = `You are not part of this {LANG:scope|l} team, therefore can only view this {LANG:scope|l} in read-only mode.`
      this.snackbarService.showPermanentSnackbar(message, SnackbarEventType.WARNING)
    }
  }

  onScopeEventReceived(event: NotificationEvent) {
    this.store
      .select(ScopeOverviewSelectors.selectCurrentScope)
      .pipe(
        take(1),
        filter((currentScope) => !!currentScope)
      )
      .subscribe((currentScope) => {
        if (!currentScope || currentScope.version !== currentScope.identity.currentVersionNumber) {
          return
        }
        switch (event.type) {
          case EventType.SCOPE_DELETED: {
            this.snackbarService.showPermanentSnackbar(
              `${this.lang.get('scope')} '` + currentScope.name + "' has been deleted by " + event.initiatedBy.fullName,
              SnackbarEventType.WARNING
            )
            this.router.navigateByUrl('/scopes')
            break
          }
          case EventType.SCOPE_ARCHIVED: {
            this.snackbarService.showPermanentSnackbar(
              `This ${this.lang.get('scope')} has been archived by ${event.initiatedBy.fullName}`,
              SnackbarEventType.WARNING
            )
            this.store.dispatch(
              ScopeOverviewActions.setScopeNotificationEvent({ scopeNotificationProps: { archived: true } })
            )
            break
          }
          case EventType.SCOPE_UNARCHIVED: {
            this.snackbarService.showPermanentSnackbar(
              `This ${this.lang.get('scope')} has been unarchived by ${event.initiatedBy.fullName}`,
              SnackbarEventType.WARNING
            )
            this.store.dispatch(
              ScopeOverviewActions.setScopeNotificationEvent({ scopeNotificationProps: { archived: false } })
            )
            break
          }
          case EventType.SCOPE_DELIVERABLE_DELETED: {
            const deliverableId = event.object as number
            const deliverables = currentScope.deliverables.filter((s) => s.id != deliverableId)
            this.store.dispatch(
              ScopeOverviewActions.setScopeNotificationEvent({
                scopeNotificationProps: { deliverables: deliverables, readyToTraffic: true },
              })
            )
            break
          }
          case EventType.SCOPE_TEAM_MEMBER_ADDED: {
            const member = event.object.member as ScopeTeamMember

            if (member.type == 'COLLABORATOR') {
              this.store.dispatch(ScopeOverviewActions.addScopeCollaboratorSuccess({ collaborators: [member] }))
            } else if (member.type == 'REVIEWER') {
              this.store.dispatch(ScopeOverviewActions.addScopeReviewerSuccess({ reviewers: [member] }))
            } else if (member.type == 'APPROVER') {
              this.store.dispatch(ScopeOverviewActions.addScopeApproverSuccess({ approvers: [member] }))
            } else if (member.type == 'TRAFFICKER') {
              this.store.dispatch(ScopeOverviewActions.addScopeTraffickerSuccess({ traffickers: [member] }))
            }
            this.getLatestAndUpdateState()
            break
          }
          case EventType.SCOPE_TEAM_MEMBER_REMOVED: {
            const member = event.object.member as ScopeTeamMember

            if (member.type == 'COLLABORATOR') {
              this.store.dispatch(ScopeOverviewActions.removeScopeCollaboratorSuccess({ member: member }))
            } else if (member.type == 'REVIEWER') {
              this.store.dispatch(ScopeOverviewActions.removeScopeReviewerSuccess({ member: member }))
            } else if (member.type == 'APPROVER') {
              this.store.dispatch(ScopeOverviewActions.removeScopeApproverSuccess({ member: member }))
            } else if (member.type == 'TRAFFICKERS') {
              this.store.dispatch(ScopeOverviewActions.removeScopeTraffickerSuccess({ member: member }))
            }
            this.getLatestAndUpdateState()
            break
          }
          case EventType.SCOPE_DELIVERABLE_ADDED: {
            this.store.dispatch(ScopeOverviewActions.addScopeDeliverableSuccess({ deliverable: event.object }))
            break
          }
          case EventType.SCOPE_COMMENT: {
            this.updateScopeComment(event.object)
            break
          }
          case EventType.SCOPE_ACTIVITY_COMMENT: {
            this.store.dispatch(ScopeOverviewActions.getActivities({ scopeId: this.currentScope.identity.id }))
            break
          }
          case EventType.SCOPE_APPROVAL_WEBHOOK_INFO_UPDATED: {
            const obj = event.object
            const scopeApprovedWebHookInfo = currentScope.scopeApprovedWebHookInfo
              ? cloneDeep(currentScope.scopeApprovedWebHookInfo)
              : {}
            scopeApprovedWebHookInfo.status = obj.status
            scopeApprovedWebHookInfo.fileId = obj.fileId
            this.store.dispatch(
              ScopeOverviewActions.setScopeNotificationEvent({
                scopeNotificationProps: { scopeApprovedWebHookInfo: scopeApprovedWebHookInfo },
              })
            )
            break
          }
          case EventType.SCOPE_UPDATED: {
            const eventScope = plainToInstance(ScopeVersion, event.object)
            const scopeNotificationProps: Partial<ScopeVersion> = {
              readyToTraffic: eventScope.readyToTraffic,
            }
            if (eventScope.dynamicFields) {
              scopeNotificationProps.dynamicFields = eventScope.dynamicFields
            }
            this.store.dispatch(ScopeOverviewActions.setScopeNotificationEvent({ scopeNotificationProps }))
            // this.store.dispatch(ScopeOverviewActions.getCurrentScope({ scopeId: this.currentScope.identity.id }))
            this.scopeApprovalFlowService.updateApprovalStateChecks(eventScope)
            break
          }
          case EventType.SCOPE_STAGE_ADDED: {
            this.store.dispatch(ScopeOverviewActions.addScopeSectionSuccess({ scopeSection: event.object }))
            break
          }
          case EventType.SCOPE_STAGE_DELETED: {
            const scopeSectionId = event.object as number
            const sections = currentScope.sections.filter((s) => s.id != scopeSectionId)
            this.store.dispatch(
              ScopeOverviewActions.setScopeNotificationEvent({
                scopeNotificationProps: { sections: sections, readyToTraffic: true },
              })
            )
            break
          }
          case EventType.SCOPE_SUBMISSION_WITHDREW: {
            const eventScope = plainToInstance(ScopeVersion, event.object)
            this.store.dispatch(
              ScopeOverviewActions.setScopeNotificationEvent({
                scopeNotificationProps: { status: eventScope.status, collaborators: eventScope.collaborators },
              })
            )
            this.scopeApprovalFlowService.updateApprovalStateChecks(eventScope)
            break
          }
          case EventType.SCOPE_SUBMITTED:
          case EventType.SCOPE_REVIEW_SUBMITTED:
          case EventType.SCOPE_APPROVAL_REJECTED:
          case EventType.SCOPE_CLIENT_APPROVAL_REJECTED:
          case EventType.SCOPE_APPROVAL_ACCEPTED:
          case EventType.SCOPE_CLIENT_APPROVAL_ACCEPTED: {
            const eventScope = plainToInstance(ScopeVersion, event.object)
            this.store.dispatch(ScopePaginatedOverviewActions.getCurrentScopeSuccess({ currentScope: eventScope }))
            this.store.dispatch(ScopeOverviewActions.getActivities({ scopeId: this.currentScope.identity.id }))
            this.scopeApprovalFlowService.updateApprovalStateChecks(eventScope)
            break
          }
          case EventType.SCOPE_CLOSED: {
            const eventScope = plainToInstance(ScopeVersion, event.object)
            this.store.dispatch(
              ScopeOverviewActions.setScopeNotificationEvent({ scopeNotificationProps: { status: eventScope.status } })
            )
            this.store.dispatch(ScopeOverviewActions.getActivities({ scopeId: this.currentScope.identity.id }))
            this.scopeApprovalFlowService.updateApprovalStateChecks(eventScope)
            break
          }
          case EventType.SCOPE_DELIVERABLE_REVIEW_REJECTED:
          case EventType.SCOPE_DELIVERABLE_REVIEW_ACCEPTED: {
            const update = plainToInstance(Deliverable, event.object)
            // Update only the event deliverable, so that notifications from accept all don't overwrite each other
            const deliverable = currentScope.deliverables.find((d) => d.id === update.id)
            this.store.dispatch(
              ScopeOverviewActions.updateScopeDeliverableSuccess({
                deliverable: plainToInstance(Deliverable, { ...deliverable, reviewers: update.reviewers }),
              })
            )
            this.store.dispatch(ScopeOverviewActions.getActivities({ scopeId: this.currentScope.identity.id }))
            this.getLatestAndUpdateState()
            break
          }
          case EventType.SCOPE_EXTERNAL_TRANSFER_STARTED: {
            this.traffickingService.total = event.object.noOfTransfers
            this.traffickingService.exportReference = event.object.exportReference
            this.traffickingService.deliverableSuccessCount = 0
            this.traffickingService.exportStatus.setInProgress()
            break
          }
          case EventType.SCOPE_EXTERNAL_TRANSFER_ABORTED: {
            this.traffickingService.abortedErrorMessage = this.traffickingService.getAbortedErrorMessage(
              event.object.errorType
            )
            this.traffickingService.errorType = event.object.errorType
            this.traffickingService.exportStatus.setExportAborted()
            break
          }
          case EventType.SCOPE_EXTERNAL_TRANSFER_COMPLETE: {
            if (event.object.successful) {
              const status = currentScope.approved ? StatusType.TRAFFICKED : currentScope.status
              const readyToTraffic = false
              const deliverables = cloneDeep(currentScope.deliverables)
              deliverables.forEach((it) => (it.readyToTraffic = false))
              const lastTraffickedDate = new Date()
              this.store.dispatch(
                ScopeOverviewActions.setScopeNotificationEvent({
                  scopeNotificationProps: {
                    status: status,
                    deliverables: deliverables,
                    readyToTraffic: readyToTraffic,
                    lastTraffickedDate: lastTraffickedDate,
                  },
                })
              )
              this.getLatestAndUpdateState()
            }
            this.scopeApprovalFlowService.stateChecks.traffickedState.isTrafficking = false
            this.traffickingService.isTrafficking = false
            this.scopeApprovalFlowService.stateChecks.traffickedState.wasTrafficking = true
            setTimeout(() => {
              this.scopeApprovalFlowService.stateChecks.traffickedState.wasTrafficking = false
            }, Timeout.L)
            this.traffickingService.deliverableSuccessCount = event.object.counter.current
            this.traffickingService.total = event.object.counter.total
            this.traffickingService.exportStatus.setExportComplete()
            break
          }
          case EventType.SCOPE_EXTERNAL_TRANSFER_DELIVERABLE_TRANSFERRED: {
            if (!this.traffickingService.isTrafficking) {
              //Refresh or newly loaded page
              this.traffickingService.isTrafficking = true
              this.scopeApprovalFlowService.stateChecks.traffickedState.isTrafficking = true
              this.traffickingService.exportStatus.setInProgress()
            }

            this.traffickingService.total = event.object.counter.total
            if (event.object.successful) {
              this.traffickingService.deliverableSuccessCount = event.object.counter.current
            } else {
              this.traffickingService.deliverableFailCount = event.object.counter.current
              const deliverable = currentScope.deliverables.find((d) => d.id == event.object.deliverableId)
              this.traffickingService.deliverableErrors.push({
                id: deliverable.id,
                name: deliverable.name,
                error: this.traffickingService.getAbortedErrorMessage(event.object.errorType),
              })
            }
            break
          }
          case EventType.CHANGE_REQUEST_TRAFFICKED: {
            if (this.currentChangeRequest) {
              this.store.dispatch(
                ScopeOverviewActions.getActivities({
                  scopeId: this.currentScope.identity.id,
                  changeRequestId: this.currentChangeRequest.id,
                })
              )
            }
            break
          }
        }
      })
  }

  updateScopeComment(object) {
    switch (object.comment.sourceType) {
      case 'DELIVERABLE': {
        this.store.dispatch(
          ScopeOverviewActions.addDeliverableCommentSuccess({
            deliverableId: object.deliverableId,
            comment: object.comment,
          })
        )
        break
      }
      case 'COMPONENT': {
        this.store.dispatch(
          ScopeOverviewActions.addComponentCommentSuccess({
            deliverableId: object.deliverableId,
            componentId: object.componentId,
            comment: object.comment,
          })
        )
        break
      }
      case 'DEPARTMENT': {
        this.store.dispatch(
          ScopeOverviewActions.addDepartmentCommentSuccess({
            deliverableId: object.deliverableId,
            componentId: object.componentId,
            departmentId: object.comment.source.id,
            comment: object.comment,
          })
        )
        break
      }
      case 'ROLE': {
        this.store.dispatch(
          ScopeOverviewActions.addRoleCommentSuccess({
            deliverableId: object.deliverableId,
            componentId: object.componentId,
            roleId: object.comment.source.id,
            comment: object.comment,
          })
        )
        break
      }
      case 'SECTION': {
        this.store.dispatch(
          ScopeOverviewActions.addDeliverableSectionCommentSuccess({
            deliverableId: object.deliverableId,
            sectionId: object.comment.source.id,
            comment: object.comment,
          })
        )
        break
      }
      case 'SCOPE_SECTION': {
        this.store.dispatch(
          ScopeOverviewActions.addScopeSectionCommentSuccess({
            sectionId: object.comment.source.id,
            comment: object.comment,
          })
        )
        break
      }
      case 'DELIVERABLE_TPC': {
        this.store.dispatch(
          ScopeOverviewActions.addDeliverableTPCCommentSuccess({
            deliverableId: object.deliverableId,
            tpcId: object.comment.source.id,
            comment: object.comment,
          })
        )
        break
      }
      case 'SCOPE_TPC': {
        this.store.dispatch(
          ScopeOverviewActions.addScopeTPCCommentSuccess({ tpcId: object.comment.source.id, comment: object.comment })
        )
        break
      }
      case 'COMPONENT_TPC': {
        this.store.dispatch(
          ScopeOverviewActions.addComponentTPCCommentSuccess({
            deliverableId: object.deliverableId,
            componentId: object.componentId,
            tpcId: object.comment.source.id,
            comment: object.comment,
          })
        )
        break
      }
      case 'SCOPE': {
        this.store.dispatch(ScopeOverviewActions.addScopeCommentSuccess({ comment: object.comment }))
        break
      }
    }
  }

  getThirdPartyBalance() {
    this.store.dispatch(ScopeOverviewActions.getThirdPartyBalance({ scopeId: this.currentScope.identity.id }))
  }

  getCdnConfig() {
    this.cdnConfig$.pipe(this.destroy$()).subscribe((config: CdnConfig) => {
      this.cdnConfig = config!
    })
  }

  private getLatestAndUpdateState = () => {
    this.store
      .select(ScopeOverviewSelectors.selectCurrentScope)
      .pipe(take(1))
      .subscribe((currentScope) => {
        this.scopeApprovalFlowService.updateApprovalStateChecks(currentScope)
      })
  }

  previewExport = (template: OutputTemplate) => {
    const dialog = this.dialog.open(PreviewOutputComponent, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%',
    })

    dialog.afterClosed().subscribe(() => document.body.classList.remove('cdk-global-scrollblock'))
    document.body.classList.add('cdk-global-scrollblock')
    dialog.componentInstance.outputTemplateName = template.name
    dialog.componentInstance.templateLanguage = template.language

    dialog.componentInstance.onDownloadExport.subscribe(() => this.openExportModal())

    this.store.dispatch(ScopeOverviewActions.previewScopeDocx({ scopeId: this.currentScope.identity.id }))
    this.store
      .select(ScopeOverviewSelectors.selectDocxPreview)
      .pipe(
        filter((response) => !!response),
        take(1)
      )
      .subscribe((response) => {
        dialog.componentInstance.documentPreview = response
        dialog.componentInstance.currentScope = this.currentScope
      })
    this.store
      .select(ScopeOverviewSelectors.selectPreviewError)
      .pipe(
        filter((error) => !!error),
        take(1)
      )
      .subscribe((error) => {
        console.error(error)
        this.snackbarService.showDefaultErrorSnackbar()
        dialog.componentInstance.loading = false
        dialog.componentInstance.errorState = true
      })
  }

  private openExportModal() {
    const dialogConfig: ExportModalConfig = {
      title: `Export ${this.lang.get('scope')}`,
      body: `Select which document file you want to output your ${this.lang.get('scope|l')}:`,
      currentScope: this.currentScope,
      currentUser: this.loggedInUser,
      limitModalWidth: true,
      scopeId: this.currentScope.identity.id,
      skipWhenSingle: true,
    }

    this.dialog.open(ExportModalComponent, { data: dialogConfig })
  }

  selectTab(tab: string) {
    this.scopeOverviewNavigationService.selectTab(tab)
  }

  confirmTab = () =>
    this.currentTab !== ScopeOverviewTab.CONFIGURATION ||
    !this.configurationHasUpdates ||
    confirm('You have unsaved changes. Press Cancel to go back and save these changes, or OK to lose these changes.')

  ngOnDestroy() {
    this.registeredSubs.forEach((s) => this.webSocketService.unregisterReceiver(s))
    this.registeredSubs = []
    this.snackbarService.hide()
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return !this.configurationHasUpdates
  }

  private readonly destroy$
  protected readonly ApplicationSetting = ApplicationSetting
  protected readonly ScopeOverviewTab = ScopeOverviewTab
}
